import React, { useState, useRef, useEffect, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { StarProvider } from './context/StarContext';
import VideoBackground from './components/VideoBackground';
import LandingScreen from './components/LandingScreen';
import PassiveScreen from './components/PassiveScreen';
import MessagesScreen from './components/MessagesScreen';
import CreateStarOverlay from './components/CreateStarOverlay';
import ConfirmationOverlay from './components/ConfirmationOverlay';
import FindStarOverlay from './components/FindStarOverlay';
import ViewYourStarScreen from './components/ViewYourStarScreen';
import AboutOverlay from './components/AboutOverlay';
import ResourcesOverlay from './components/ResourcesOverlay';
import MenuOverlay from './components/MenuOverlay';
import PrivacyPolicyOverlay from './components/PrivacyPolicyOverlay';
import StillLovedLogo from './assets/StillLovedLogo_Medicina.svg';
import { Link } from 'react-router-dom';
import Layout from './components/Layout';
import { AnimatePresence } from 'framer-motion';
import StillLovedMasterOverlay from './components/StillLovedMasterOverlay';
import { videoConfigs } from './config/videos';
import { useVideoTransition } from './hooks/useVideoTransition';
import { useAudio } from './hooks/useAudio';
import ContentOverlay from './components/ContentOverlay';
import AdminPanel from './components/AdminPanel';
import AdminLogin from './components/AdminLogin';
import ProtectedRoute from './components/ProtectedRoute';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';

// Create a context for the master overlay visibility
export const MasterOverlayContext = createContext();

// Custom hook to use the master overlay context
export const useOverlay = () => {
    const context = useContext(MasterOverlayContext);
    if (!context) {
        throw new Error('useOverlay must be used within a MasterOverlayProvider');
    }
    return context;
};

const SoundPrompt = ({ onClick }) => (
    <motion.div 
        className="hidden md:block absolute top-20 right-4 z-[200] pointer-events-auto cursor-pointer"
        onClick={onClick}
    >
        <div className="bg-black/30 backdrop-blur-md rounded-lg px-6 py-3 text-white text-sm">
            CLICK TO ENABLE SOUND
        </div>
    </motion.div>
);

function App() {
    const [menuOpen, setMenuOpen] = useState(false);
    const [overlayVisible, setOverlayVisible] = useState(false); // Default to false
    const [privacyPolicyOpen, setPrivacyPolicyOpen] = useState(false); // State for privacy policy overlay

    // Debug overlay state changes
    useEffect(() => {
        //console.log('App: overlayVisible state changed to', overlayVisible);
    }, [overlayVisible]);

    const toggleMenu = () => {
        setMenuOpen(!menuOpen);
    };

    // Function to show/hide the master overlay
    const toggleOverlay = (visible) => {
        ////console.log('App: toggleOverlay called with', visible);
        
        // Check if we're on mobile AND on the messages screen - only then prevent showing overlay
        const isMessagesScreen = window.location.pathname === '/messages';
        if (visible && typeof window !== 'undefined' && window.innerWidth < 800 && isMessagesScreen) {
            //console.log('App: Preventing overlay on mobile for messages screen');
            setOverlayVisible(false);
            return;
        }
        
        // If we're showing the overlay, also reset the animation state
        if (visible) {
            // Reset animation state in sessionStorage
            sessionStorage.removeItem('animationCompleted');
            sessionStorage.removeItem('lastDisplayedCount');
            
            // Set a new animation start time - add a small delay to ensure it's in the future
            const now = Date.now();
            sessionStorage.setItem('animationStartTime', (now + 100).toString());
        }
        
        setOverlayVisible(visible);
    };

    // Function to toggle privacy policy overlay
    const togglePrivacyPolicy = () => {
        setPrivacyPolicyOpen(!privacyPolicyOpen);
    };

    // Add CSS for the content overlay
    useEffect(() => {
        // Add custom styles for the content overlay
        const style = document.createElement('style');
        style.textContent = `
            /* When content is fading out, prevent interaction with the UI except for the menu and logo */
            body.content-fading-out .fixed:not(.pointer-events-auto):not([class*="z-[100]"]):not([class*="z-[150]"]) {
                pointer-events: none !important;
            }
            
            /* When content is fading out, make only the main content invisible */
            body.content-fading-out main,
            body.content-fading-out .fixed:not(#transition-video-overlay):not([class*="z-[100]"]):not([class*="z-[150]"]):not([class*="z-20"]) {
                opacity: 0 !important;
                transition: opacity 0.5s ease-in-out !important;
            }
            
            /* Keep the menu and logo visible */
            body.content-fading-out [class*="z-[100]"],
            body.content-fading-out [class*="z-[150]"],
            body.content-fading-out [class*="z-20"] {
                opacity: 1 !important;
                z-index: 10000 !important; /* Higher than the transition video */
            }
            
            /* Ensure the transition video overlay is always visible */
            #transition-video-overlay {
                position: fixed !important;
                top: 0 !important;
                left: 0 !important;
                width: 100% !important;
                height: 100% !important;
                object-fit: cover !important;
                z-index: 9999 !important;
                opacity: 1 !important;
                visibility: visible !important;
                display: block !important;
                background-color: black !important;
                animation: fadeIn 0.5s ease-in-out !important;
                disablePictureInPicture: true !important;
            }
            
            /* Disable Picture-in-Picture for all videos during transitions */
            body.content-fading-out video {
                disablePictureInPicture: true !important;
            }
            
            /* Ensure the logo is visible above the transition video */
            body.content-fading-out .fixed.top-0.left-0.right-0.z-20 {
                z-index: 10000 !important;
                opacity: 1 !important;
                visibility: visible !important;
            }
            
            /* Ensure the menu button is visible above the transition video */
            body.content-fading-out .fixed.top-4.right-6.z-\\[100\\] {
                z-index: 10000 !important;
                opacity: 1 !important;
                visibility: visible !important;
            }
            
            /* Ensure the audio toggle is visible above the transition video */
            body.content-fading-out .fixed.top-\\[42px\\].right-20.z-\\[100\\] {
                z-index: 10000 !important;
                opacity: 1 !important;
                visibility: visible !important;
            }
            
            /* Animation for fading in the transition video */
            @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
            }
        `;
        document.head.appendChild(style);
        
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    return (
        <Router>
            <MasterOverlayContext.Provider value={{ overlayVisible, toggleOverlay }}>
                <StarProvider>
                    <AppContent 
                        menuOpen={menuOpen} 
                        toggleMenu={toggleMenu} 
                        overlayVisible={overlayVisible}
                        setOverlayVisible={setOverlayVisible}
                        privacyPolicyOpen={privacyPolicyOpen}
                        togglePrivacyPolicy={togglePrivacyPolicy}
                    />
                </StarProvider>
            </MasterOverlayContext.Provider>
        </Router>
    );
}

// Audio Switcher 
const AudioSwitcher = ({ audioMuted, toggleAudio }) => {
    return (
        <button 
            id="switch-audio"
            onClick={toggleAudio}
            className={`audio-icon ${audioMuted ? 'muted' : ''}`}
            aria-label={audioMuted ? "Unmute audio" : "Mute audio"}
            style={{ transform: 'scale(2)', transformOrigin: 'center center' }}
        >
            <span className="bar bar-1"></span>
            <span className="bar bar-2"></span>
            <span className="bar bar-3"></span>
        </button>
    );
};

function AppContent({ 
    menuOpen, 
    toggleMenu, 
    overlayVisible, 
    setOverlayVisible,
    privacyPolicyOpen,
    togglePrivacyPolicy
}) {
    const location = useLocation();
    const { handleNavigate } = useVideoTransition();
    const { audioMuted, toggleAudio } = useAudio();
    const [showSoundPrompt, setShowSoundPrompt] = useState(() => {
        return localStorage.getItem('audioUnmuted') !== 'true';
    });

    const handleSoundPromptClick = (e) => {
        e.stopPropagation();
        setShowSoundPrompt(false);
        if (audioMuted) {
            toggleAudio();
        }
    };

    // Update overlay visibility when location changes
    useEffect(() => {
        // Check if we're on mobile AND on the messages screen - only then prevent showing overlay
        const isMessagesScreen = location.pathname === '/messages';
        if (typeof window !== 'undefined' && window.innerWidth < 800 && isMessagesScreen) {
            setOverlayVisible(false);
            //console.log('App: Preventing overlay on mobile for messages screen');
            return;
        }
        
        // Check for the sessionStorage flag
        const shouldBeVisible = sessionStorage.getItem('overlayVisible') === 'true';
        
        // Check if this is a page refresh
        const isRefresh = sessionStorage.getItem('isRefresh') === 'true';
        
        // Only show overlay on passive screen by default
        // The LandingScreen component will manage its own overlay visibility
        if (location.pathname === '/space') {
            setOverlayVisible(true);
            //console.log('App: Setting overlay visible for passive screen');
        } else if (location.pathname !== '/') {
            // For all other routes except landing page, hide the overlay
            setOverlayVisible(false);
            //console.log('App: Setting overlay hidden for route:', location.pathname);
        } else {
            // This allows the LandingScreen component to control it
            setOverlayVisible(false);
            //console.log('App: Landing page detected, overlay visibility controlled by LandingScreen');
        }
    }, [location.pathname, setOverlayVisible]);
    
    // Helper function to get page title
    const getPageTitle = (pathname) => {
        switch (pathname) {
            case '/':
                return 'Still Loved';
            case '/space':
                return 'Still Loved Nebula';
            case '/create-star':
                return 'Still Loved - Create a Star';
            case '/find-star':
                return 'Still Loved - Find a Star';
            case '/about':
                return 'Still Loved - About';
            case '/support':
                return 'Still Loved - Support';
            case '/messages':
                return 'Still Loved - Stars';
            case '/admin':
                return 'Admin Panel - Still Loved';
            default:
                if (pathname.startsWith('/view-star/')) {
                    return 'Still Loved - View Star';
                }
                if (pathname.startsWith('/confirmation/')) {
                    return 'Still Loved - Star Created';
                }
                return 'Still Loved';
        }
    };
    
    return (
        <>
            <Helmet defaultTitle={getPageTitle(location.pathname)} titleTemplate="%s" />
            <VideoBackground />
            
            {showSoundPrompt && <SoundPrompt onClick={handleSoundPromptClick} />}
            
            <StillLovedMasterOverlay visible={overlayVisible} />
            
            <ContentOverlay />
            
            <PrivacyPolicyOverlay isOpen={privacyPolicyOpen} onClose={togglePrivacyPolicy} />
            
            {/* Logo and burger menu outside the Layout to exclude from 3D effect */}
            <div className="fixed inset-0 pointer-events-none z-[150]">
                <div className="pointer-events-auto">
                    {/* Audio toggle button */}
                    <div className="fixed top-[42px] right-20 z-[100] flex items-center justify-center">
                        <AudioSwitcher audioMuted={audioMuted} toggleAudio={toggleAudio} />
                    </div>
                    
                    {/* Burger menu button */}
                    <button 
                        onClick={toggleMenu}
                        className={`fixed top-4 right-6 z-[100] focus:outline-none ${menuOpen ? 'open' : ''}`}
                        aria-label={menuOpen ? "Close menu" : "Open menu"}
                    >
                        <div className="w-8 h-0.5 bg-white line-glow-still my-3 transition-transform duration-300 transform origin-center"></div>
                        <div className="w-8 h-0.5 bg-white line-glow-still my-3 transition-transform duration-300 transform origin-center"></div>
                        <div className="w-8 h-0.5 bg-white line-glow-still my-3 transition-transform duration-300 transform origin-center"></div>
                    </button>
                    <div className="fixed top-0 left-0 right-0 z-20 flex p-6">
                        <Link 
                            to="/" 
                            onClick={(e) => {
                                e.preventDefault(); // Prevent default navigation
                                
                                // Reset the overlay animation state when navigating to home page
                                sessionStorage.removeItem('animationCompleted');
                                sessionStorage.removeItem('lastDisplayedCount');
                                
                                // Set overlay to be visible when returning to home page
                                sessionStorage.setItem('overlayVisible', 'true');
                                
                                // Use our custom navigation
                                handleNavigate('/');
                            }}
                        >
                            <img 
                                src={StillLovedLogo} 
                                alt="Still Loved & Medicina Logo" 
                                className="w-52 sm:w-52 md:w-52 lg:w-120" 
                            />
                        </Link>
                    </div>
                </div>
                {/* Menu overlay - outside the 3D effect */}
                <div className="pointer-events-auto">
                    <MenuOverlay isOpen={menuOpen} onClose={toggleMenu} />
                </div>
            </div>

            {/* Trigger Warning text - fixed at bottom left with responsive positioning */}
            <div className="fixed bottom-3 left-4 sm:left-6 z-[200] pointer-events-none">
                <p className="text-white text-xs opacity-70 hover:opacity-100 transition-opacity max-w-[250px] sm:max-w-[500px] md:max-w-[900px]">
                This website addresses the sensitive topic of early baby loss. We understand this may be emotional for some visitors. Please take care while exploring.
                </p>
            </div>
            
            {/* Privacy Policy button - fixed at bottom right */}
            <div className="fixed bottom-6 right-6 z-[200] pointer-events-auto">
                <button 
                    onClick={togglePrivacyPolicy}
                    className="text-white text-xs opacity-70 hover:opacity-100 transition-opacity"
                    aria-label="Privacy Policy"
                >
                    Privacy Policy
                </button>
            </div>
            
            {/* Main content with 3D effect */}
            <Layout menuOpen={menuOpen} toggleMenu={toggleMenu}>
                <div className="fixed inset-0 flex flex-col">
                    <main className="flex-1 overflow-y-auto mask-none 
                        max-sm:[mask-image:linear-gradient(to_bottom,transparent_0%,transparent_66px,white_120px,white)]"
                    >
                        <AnimatePresence mode="wait">
                            <div className="relative z-10 h-full mix-blend-screen">
                                <Routes>
                                    <Route 
                                        path="/" 
                                        exact 
                                        element={
                                            <LandingScreen 
                                                audioMuted={audioMuted} 
                                                toggleAudio={toggleAudio}
                                                showSoundPrompt={showSoundPrompt}
                                                setShowSoundPrompt={setShowSoundPrompt}
                                            />
                                        } 
                                    />
                                    <Route path="/space" element={<PassiveScreen />} />
                                    <Route path="/create-star" element={<CreateStarOverlay />} />
                                    <Route path="/confirmation/:starId" element={<ConfirmationOverlay />} />
                                    <Route path="/find-star" element={<FindStarOverlay />} />
                                    <Route path="/view-star/:starId" element={<ViewYourStarScreen />} />
                                    <Route path="/about" element={<AboutOverlay />} />
                                    <Route path="/support" element={<ResourcesOverlay />} />
                                    <Route path="/messages" element={<MessagesScreen />} />
                                    <Route path="/admin/login" element={<AdminLogin />} />
                                    <Route 
                                        path="/admin" 
                                        element={
                                            <ProtectedRoute>
                                                <AdminPanel />
                                            </ProtectedRoute>
                                        } 
                                    />
                                    <Route path="*" element={() => <div>404 Not Found</div>} />
                                </Routes>
                            </div>
                        </AnimatePresence>
                    </main>
                </div>
            </Layout>
        </>
    );
}

export default App;